/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { BackHandler, Platform, View } from 'react-native';
import HeaderView from '@airasia-phoenix/react-native/widgets/HeaderView';
import { CurrencyListView } from '../../components/CurrencyList';
import { pd } from '@airasia-phoenix/react-native/utils';
import { BottomSheetRefs } from '@airasia-phoenix/react-native/components';
import type { IconViewProps } from '@airasia-phoenix/react-native/components/IconView';
import { type SupportedCurrencies, useCurrency } from '@airasia-rn/ui/config';
import { HeaderProps } from './types';
import CurrencyExchange from '@move-travel/react-native-phoenix-icons/CurrencyExchange';
import Edit from '@move-travel/react-native-phoenix-icons/Edit';
import Share from '@move-travel/react-native-phoenix-icons/Share';

const DEFAULT_MAX_SUPPORTING_TEXT_LENGTH = 35;

const Header = (props: HeaderProps) => {
  const {
    onCurrencySelect,
    onShareIconPress,
    onHeaderBack,
    titleStart,
    titleIcon,
    titleEnd,
    supportingText,
    maxSupportingTextLength = DEFAULT_MAX_SUPPORTING_TEXT_LENGTH,
    onEditIconPress,
    startIconToken,
    startIcon,
    currencyList,
  } = props;
  const { currency } = useCurrency();
  const bottomSheetCurrencyRef = useRef<BottomSheetRefs>(null);

  const currencyOpenRef = useRef(false);

  const truncatedSupportingText =
    supportingText.length > maxSupportingTextLength
      ? `${supportingText.slice(0, maxSupportingTextLength - 3)}...`
      : supportingText;

  const openCurrency = useCallback(() => {
    if (currencyOpenRef.current) return;
    currencyOpenRef.current = true;
    bottomSheetCurrencyRef.current?.toggleBottomSheetView();
  }, []);

  const requestCloseCurrency = useCallback(() => {
    // close because USER asked to close (button/select)
    if (!currencyOpenRef.current) return;
    currencyOpenRef.current = false;
    bottomSheetCurrencyRef.current?.toggleBottomSheetView(); // dismiss
  }, []);

  const onCurrencyDismissed = useCallback(() => {
    // close happened already (swipe/backdrop/dismiss)
    currencyOpenRef.current = false;
  }, []);

  // Android hardware back should close the currency sheet first instead of
  // falling through to the page's own back navigation (e.g. skipping
  // straight from the Details page to the Listing page). This listener is
  // always registered while the header is mounted (not just while the sheet
  // is open) since currencyOpenRef is a ref, not state — it's a no-op
  // (returns false) whenever the sheet isn't open.
  useEffect(() => {
    if (Platform.OS === 'web') {
      return undefined;
    }

    const handleHardwareBackPress = () => {
      if (!currencyOpenRef.current) {
        return false;
      }

      requestCloseCurrency();
      return true;
    };

    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      handleHardwareBackPress,
    );

    return () => subscription.remove();
  }, [requestCloseCurrency]);

  const handleCurrencySelect = useCallback(
    (currencyObj: { id?: SupportedCurrencies }) => {
      const currencyCode = currencyObj?.id;

      if (!onCurrencySelect || !currencyCode) {
        return;
      }

      requestCloseCurrency();
      onCurrencySelect(currencyCode);
    },
    [onCurrencySelect, requestCloseCurrency],
  );

  const endActions = useMemo(() => {
    const actions = [];

    if (onEditIconPress) {
      actions.push({
        icon: {
          children: <Edit />,
        },
        onPress: onEditIconPress,
      });
    }

    if (onShareIconPress) {
      actions.push({
        icon: {
          children: <Share />,
        },
        onPress: onShareIconPress,
      });
    }

    if (onCurrencySelect) {
      actions.push({
        icon: {
          children: <CurrencyExchange />,
        },
        onPress: openCurrency,
      });
    }

    return actions;
  }, [onEditIconPress, onShareIconPress, onCurrencySelect, openCurrency]);

  return (
    <View>
      <HeaderView
        startIcon={{
          icon: startIcon ? { children: startIcon } : { icon: startIconToken },
          onPress() {
            onHeaderBack();
          },
        }}
        titleStart={titleStart}
        titleIcon={titleIcon}
        titleEnd={titleEnd}
        // Phoenix renders `supportingText && <View>`, so '' would leak an empty
        // text node into a View (RNW "Unexpected text node" warning).
        supportingText={truncatedSupportingText || undefined}
        endActions={endActions}
      />

      {onCurrencySelect && (
        <CurrencyListView
          bottomSheetViewRef={bottomSheetCurrencyRef}
          onDismissed={onCurrencyDismissed}
          requestClose={requestCloseCurrency}
          selectedCurrency={currency}
          onSelect={handleCurrencySelect}
          currencyList={currencyList}
        />
      )}
    </View>
  );
};

export default Header;
